import { createReducer } from '@reduxjs/toolkit'
import {
  BuildingStructuralTypes,
  StructuralSettings,
} from '../models/Structural'
import {
  endStructuralAnimation,
  playStructuralAnimation,
  resetStructuralState,
  setStructuralSettings,
  showLoader,
  showStructuralSettingsModal,
  toggleStructuralType,
} from '../actions/structural'
import { getEnums } from '../utils/Common'

export enum StructuralAnimationStatus {
  Pending,
  InProgress,
  Completed,
}

interface StructuralState {
  selectedStructuralTypes: BuildingStructuralTypes[]
  structuralAnimationStatus: StructuralAnimationStatus
  objectSettings: StructuralSettings[]
  showStructuralSettingsModal: boolean
  defaultStructuralSettings: StructuralSettings[]
  showLoader?: boolean
}

const initialState: StructuralState = {
  selectedStructuralTypes: getEnums(BuildingStructuralTypes).map(
    ([_, value]) => value,
  ),
  structuralAnimationStatus: StructuralAnimationStatus.Pending,
  objectSettings: [],
  showStructuralSettingsModal: false,
  defaultStructuralSettings: [
    {
      category: 'General Tolerances',
      category_id: 'tolerance_params',
      variable: 'ABSOLUTE_TOLERANCE',
      value: 0.1,
      unit: '',
    },
    {
      category: 'General Tolerances',
      category_id: 'tolerance_params',
      variable: 'TOL_POLY_TO_RECT',
      value: 1,
      unit: '',
    },
    {
      category: 'Span & Beam Limits',
      category_id: 'gravity_params',
      variable: 'SPAN_THICKNESS',
      value: 12,
      unit: 'inch',
    },
    {
      category: 'Span & Beam Limits',
      category_id: 'gravity_params',
      variable: 'MAX_SPAN_LENGTH',
      value: 300,
      unit: 'inch',
    },
    {
      category: 'Span & Beam Limits',
      category_id: 'gravity_params',
      variable: 'MAX_BEAM_LENGTH',
      value: 240,
      unit: 'inch',
    },
    {
      category: 'Beam Properties',
      category_id: 'framing_params',
      variable: 'BEAM_DEPTH',
      value: 12,
      unit: 'inch',
    },
    {
      category: 'Beam Properties',
      category_id: 'framing_params',
      variable: 'BEAM_THICKNESS',
      value: 4,
      unit: 'inch',
    },
    {
      category: 'Joist Properties',
      category_id: 'framing_params',
      variable: 'JOIST_WIDTH',
      value: 12,
      unit: 'inch',
    },
    {
      category: 'Joist Properties',
      category_id: 'framing_params',
      variable: 'JOIST_THICKNESS',
      value: 2,
      unit: 'inch',
    },
    {
      category: 'Joist Properties',
      category_id: 'framing_params',
      variable: 'JOIST_SPACING',
      value: 12,
      unit: 'inch',
    },
    {
      category: 'Stud Properties',
      category_id: 'framing_params',
      variable: 'STUD_THICKNESS',
      value: 2,
      unit: 'inch',
    },
    {
      category: 'Stud Properties',
      category_id: 'framing_params',
      variable: 'LB_STUDS_SPACING',
      value: 16,
      unit: 'inch',
    },
    {
      category: 'Stud Properties',
      category_id: 'framing_params',
      variable: 'NB_STUDS_SPACING',
      value: 16,
      unit: 'inch',
    },
    {
      category: 'Header Specifications',
      category_id: 'framing_params',
      variable: 'HEADER_THICKNESS',
      value: 2,
      unit: 'inch',
    },
    {
      category: 'Header Specifications',
      category_id: 'framing_params',
      variable: 'MAX_HEADER_WIDTH',
      value: 12,
      unit: 'inch',
    },
    //   {
    //     "category": "Header Specifications",
    //     "variable": "HEADER_WIDTH_THRESHOLDS",
    //     "DefaultNominaValue": [[48, 4], [72, 6], [96, 8], [120, 10], [144, 12]],
    //     "value": [[48, 3.5], [72, 5.5], [96, 7.5], [120, 9.5], [144, 11.5]],
    //     "unit": "inch"
    //   },
    {
      category: 'Wall Plate Dimensions',
      category_id: 'framing_params',
      variable: 'TOP_PLATE_THICKNESS',
      value: 2,
      unit: 'inch',
    },
    {
      category: 'Wall Plate Dimensions',
      category_id: 'framing_params',
      variable: 'BOT_PLATE_THICKNESS',
      value: 2,
      unit: 'inch',
    },
    {
      category: 'Wall Plate Dimensions',
      category_id: 'framing_params',
      variable: 'SILL_PLATE_THICKNESS',
      value: 2,
      unit: 'inch',
    },
    {
      category: 'Cripple Studs',
      category_id: 'framing_params',
      variable: 'CRIPPLE_SPACING',
      value: 16,
      unit: 'inch',
    },
    {
      category: 'Wall Parameters',
      category_id: 'load_params',
      variable: 'COMMON_WALL_THICKNESS',
      value: 6,
      unit: 'inch',
    },
    {
      category: 'Wall Parameters',
      category_id: 'load_params',
      variable: 'WALL_DEAD_LOAD',
      value: 5,
      unit: 'psf',
    },
    {
      category: 'Rafter Specs',
      category_id: 'framing_params',
      variable: 'RAFTER_WIDTH',
      value: 12,
      unit: 'inch',
    },
    {
      category: 'Rafter Specs',
      category_id: 'framing_params',
      variable: 'RAFTER_THICKNESS',
      value: 2,
      unit: 'inch',
    },
    {
      category: 'Rafter Specs',
      category_id: 'framing_params',
      variable: 'RAFTER_MAX_SPACING',
      value: 16,
      unit: 'inch',
    },
    {
      category: 'Rafter Specs',
      category_id: 'framing_params',
      variable: 'RAFTER_MIN_SPACING',
      value: 12,
      unit: 'inch',
    },
    {
      category: 'Post Dimensions',
      category_id: 'framing_params',
      variable: 'POST_WIDTH',
      value: 4,
      unit: 'inch',
    },
    {
      category: 'Post Dimensions',
      category_id: 'framing_params',
      variable: 'POST_THICKNESS',
      value: 4,
      unit: 'inch',
    },
    {
      category: 'Post Dimensions',
      category_id: 'gravity_params',
      variable: 'MIN_POST_SPAN',
      value: 120,
      unit: 'inch',
    },
    {
      category: 'Post Dimensions',
      category_id: 'gravity_params',
      variable: 'MAX_POST_SPAN',
      value: 240,
      unit: 'inch',
    },
    {
      category: 'Post Dimensions',
      category_id: 'gravity_params',
      variable: 'CANTILIVER_POST_SPAN',
      value: 24,
      unit: 'inch',
    },
    {
      category: 'Ridge Extraction',
      category_id: 'internal_params',
      variable: 'RIDGE_EXTRACTION_ERROR',
      value: 3,
      unit: 'inch',
    },
    {
      category: 'Load Defaults',
      category_id: 'load_params',
      variable: 'AREA_DEAD_LOAD',
      value: 20,
      unit: 'psf',
    },
    {
      category: 'Load Defaults',
      category_id: 'load_params',
      variable: 'AREA_LIVE_LOAD',
      value: 40,
      unit: 'psf',
    },
    {
      category: 'Load Defaults',
      category_id: 'internal_params',
      variable: 'STANDARD_BEDROOM_AREA',
      value: 120,
      unit: 'ft²',
    },
    {
      category: 'Seismic Analysis',
      category_id: 'shear_wall_params',
      variable: 'SEISMIC_DESIGN_FACTOR',
      value: 0.3,
      unit: '',
    },
    {
      category: 'Seismic Analysis',
      category_id: 'shear_wall_params',
      variable: 'REDUCTION_FORCE_FACTOR',
      value: 0.7,
      unit: '',
    },
    {
      category: 'Shear Wall Specs',
      category_id: 'shear_wall_params',
      variable: 'SHEAR_WALL_CAPACITY',
      value: 665,
      unit: 'plf',
    },
    {
      category: 'Shear Wall Specs',
      category_id: 'shear_wall_params',
      variable: 'MAX_STRIP_WIDTH',
      value: 120,
      unit: 'inch',
    },
    {
      category: 'Shear Wall Specs',
      category_id: 'shear_wall_params',
      variable: 'SPACING_STRIP_RATIO',
      value: 3,
      unit: '',
    },
    {
      category: 'Shear Wall Specs',
      category_id: 'shear_wall_params',
      variable: 'MIN_SHEAR_WALL_LENGTH',
      value: 30,
      unit: 'inch',
    },
  ],
  showLoader: false,
}

const structuralReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(toggleStructuralType, (state, action) => {
      const set = new Set(state.selectedStructuralTypes)
      const item = action.payload.item

      if (set.has(item)) {
        set.delete(item)
      } else {
        set.add(item)
      }

      state.selectedStructuralTypes = Array.from(set)
    })
    .addCase(playStructuralAnimation, (state) => {
      state.structuralAnimationStatus = StructuralAnimationStatus.InProgress
    })
    .addCase(endStructuralAnimation, (state) => {
      state.structuralAnimationStatus = StructuralAnimationStatus.Completed
    })
    .addCase(setStructuralSettings, (state, action) => {
      state.objectSettings = action.payload
    })
    .addCase(showStructuralSettingsModal, (state, action) => {
      state.showStructuralSettingsModal = action.payload
    })
    .addCase(showLoader, (state, action) => {
      state.showLoader = action.payload
    })
    .addCase(resetStructuralState, () => {
      return initialState
    })
})

export default structuralReducer
